import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonService } from '../../../../../../shared/services/common.service';
import { MasterManagementService } from '../../../../../management/master-management/master-management.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-roles',
  templateUrl: './roles.component.html',
  styleUrls: ['./roles.component.css']
})
export class RolesComponent implements OnInit {
  customer_id: string;
  public roles: any[] = [];
  public displayRoles: any[] = [];

  isFormModalVisible: boolean = false;
  isLoading: boolean = false;

  user_id = localStorage.getItem('user_id');
  roleForm!: FormGroup;

  privilegeOptions: any = []

  // Edit mode tracking
  isEditMode: boolean = false;
  editingRole: any = null;
  originalRoleData: any = null;
  blockedDefaultRoleMessageForId: number | null = null;

  listOfColumns = [{
    name: 'Role Id'
  }, {
    name: 'Role Name'
  }, {
    name: 'Privilege'
  }, {
    name: 'Action'
  }]



  constructor(
    private commonService: CommonService,
    private masterManagementService: MasterManagementService,
    public route: ActivatedRoute,
    private fb: FormBuilder
  ) {
    this.customer_id = this.route.snapshot.paramMap.get('id') || '';
  }

  ngOnInit() {
    this.initializeForm();
    this.getRolesByCustomerId();
  }

  isDefaultRole(role: any): boolean {
    const cid = role?.customer_id;
    if (cid == null) return false;
    const n = typeof cid === 'string' ? Number.parseInt(cid, 10) : Number(cid);
    return n === -1;
  }

  private computeDisplayRoles(roles: any[]): any[] {
    const groups = new Map<string, any[]>();
    for (const role of roles || []) {
      const key = (role?.name ?? '').toString();
      const arr = groups.get(key);
      if (arr) arr.push(role);
      else groups.set(key, [role]);
    }

    const output: any[] = [];
    const handled = new Set<string>();
    for (const role of roles || []) {
      const key = (role?.name ?? '').toString();
      if (handled.has(key)) continue;
      handled.add(key);

      const group = groups.get(key) || [];
      if (group.length > 1) {
        const defaults = group.filter(r => this.isDefaultRole(r));
        output.push(...defaults);
      } else {
        output.push(role);
      }
    }
    return output;
  }

  initializeForm(): void {
    this.roleForm = this.fb.group({
      roleName: [null, [Validators.required]],
      privilege: [null, [Validators.required]]
    });
  }

  openFormModal() {
    // Open in add mode by default
    this.isEditMode = false;
    this.editingRole = null;
    this.originalRoleData = null;
    this.roleForm.reset();
    this.isFormModalVisible = true;
    this.getPrivilegeOptions();
  }

  /**
   * Open the form modal in edit mode with prefilled values
   */
  onEditRole(role: any) {
    if (!role) {
      return;
    }

    this.isEditMode = true;
    this.editingRole = role;

    // Prefill form with existing values
    this.roleForm.patchValue({
      roleName: role.name,
      privilege: role.privilege
    });

    // Store original data for change detection
    this.originalRoleData = {
      role_name: role.name,
      privilege: role.privilege
    };

    this.isFormModalVisible = true;
    this.getPrivilegeOptions();
  }

  onEditRoleClick(role: any, event?: Event) {
    event?.stopPropagation();
    if (this.isDefaultRole(role)) {
      this.blockedDefaultRoleMessageForId = role?.id ?? null;
      window.setTimeout(() => {
        if (this.blockedDefaultRoleMessageForId === (role?.id ?? null)) {
          this.blockedDefaultRoleMessageForId = null;
        }
      }, 2500);
      return;
    }
    this.onEditRole(role);
  }

  getPrivilegeOptions() {
    this.masterManagementService.getRolesByCustomerId(-1).subscribe({
      next: (res: any) => {
        if (res.status == 200) {
          this.privilegeOptions = res.data.map((role: any) => ({
            roleName: role.name,
            priv: role.privilege
          }));
        }
      },
      error: (err: any) => {
        console.error('Error fetching roles:', err);
      }
    });
  }

  getRolesByCustomerId() {
    this.isLoading = true;
    this.masterManagementService.getRolesByCustomerId(this.customer_id).subscribe({
      next: (res: any) => {
        if (res.status == 200) {
          this.roles = res.data;
          this.displayRoles = this.computeDisplayRoles(this.roles);
          const privilegeOptions = this.roles.map((role: any) => ({
            roleName: role.name,
            priv: role.privilege
          }));
          this.privilegeOptions = privilegeOptions;
        }
        this.isLoading = false;
      },
      error: (err: any) => {
        console.error('Error fetching roles:', err);
        this.isLoading = false;
      }
    });
  }

  deleteRole(id: number) {
    this.isLoading = true;
    this.masterManagementService.deleteRole(id).subscribe({
      next: (res: any) => {
        if (res?.status === 200) {
          this.commonService.message.success('Role deleted successfully');
          this.getRolesByCustomerId();
        } else {
          this.commonService.message.error(res?.message || 'Failed to delete role');
        }
        this.isLoading = false;
      },
      error: (err: any) => {
        console.error('Error deleting role:', err);
        this.commonService.message.error(err?.error?.message || err?.message || 'Failed to delete role');
        this.isLoading = false;
      }
    });
  }

  onDeleteConfirm(role: any) {
    const id = Number(role?.id);
    if (!Number.isFinite(id)) return;
    this.deleteRole(id);
  }

  onDeleteRoleClick(role: any, event?: Event) {
    event?.stopPropagation();
    if (this.isDefaultRole(role)) {
      this.blockedDefaultRoleMessageForId = role?.id ?? null;
      window.setTimeout(() => {
        if (this.blockedDefaultRoleMessageForId === (role?.id ?? null)) {
          this.blockedDefaultRoleMessageForId = null;
        }
      }, 2500);
    }
  }

  submitForm(): void {
    if (this.roleForm.valid) {
      this.isLoading = true;
      const payload = {
        role_name: this.roleForm.value.roleName,
        privilege: this.roleForm.value.privilege
      };

      if (this.isEditMode && this.editingRole && this.editingRole.id != null) {
        // Edit mode: send only changed fields via PATCH with role id as param
        const changedFields = this.commonService.getChangedFields(
          this.originalRoleData || {},
          payload
        );

        if (Object.keys(changedFields).length === 0) {
          this.commonService.message.info('No changes detected');
          this.isLoading = false;
          return;
        }

        this.masterManagementService.updateRole(this.editingRole.id, this.customer_id, changedFields).subscribe({
          next: (res: any) => {
            if (res.status === 200) {
              this.getRolesByCustomerId();
              this.isFormModalVisible = false;
              this.roleForm.reset();
              this.isEditMode = false;
              this.editingRole = null;
              this.originalRoleData = null;
              this.commonService.message.success('Role updated successfully');
            } else {
              this.commonService.message.error(res.message || 'Failed to update role');
            }
            this.isLoading = false;
          },
          error: (err: any) => {
            console.error('Error updating role:', err);
            const errorMessage = err?.error?.message || err?.message || 'An error occurred while updating the role';
            this.commonService.message.error(errorMessage);
            this.isLoading = false;
          }
        });
      } else {
        // Add mode: create new role
        const roleData = {
          ...payload,
          customer_id: this.customer_id,
          createdBy: this.user_id
        };
        this.masterManagementService.createRole(roleData).subscribe({
          next: (res: any) => {
            if (res.status === 200) {
              this.getRolesByCustomerId();
              this.isFormModalVisible = false;
              this.roleForm.reset();
              this.commonService.message.success('Role added successfully');
            } else {
              this.commonService.message.error(res.message);
            }
            this.isLoading = false;
          },
          error: (err: any) => {
            console.error('Error creating role:', err);
            const errorMessage = err?.error?.message || err?.message || 'An error occurred while creating the role';
            this.commonService.message.error(errorMessage);
            this.isLoading = false;
          }
        });
      }
    } else {
      Object.values(this.roleForm.controls).forEach(control => {
        control.markAsDirty();
        control.updateValueAndValidity();
      });
    }
  }

}
